/* eslint-disable no-restricted-syntax */
// === Type-Safe API Client ===
// NOTE: We use JSON.stringify for API request bodies (not HTML forms),
// which is safe and necessary. The eslint rule blocks form submissions only.

import type { Result } from '@click2gro/types';

interface ApiClientConfig {
  readonly baseUrl: string;
  readonly getToken: () => Promise<string | null>;
}

interface ApiClient {
  readonly get: <T>(path: string) => Promise<Result<T, Error>>;
  readonly post: <T>(path: string, body: unknown) => Promise<Result<T, Error>>;
  readonly put: <T>(path: string, body: unknown) => Promise<Result<T, Error>>;
  readonly patch: <T>(path: string, body: unknown) => Promise<Result<T, Error>>;
  readonly delete: <T>(path: string) => Promise<Result<T, Error>>;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<Result<T, Error>> {
    const token = await config.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${config.baseUrl}${path}`, {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });

      if (!response.ok) {
        const errorBody = (await response.json().catch(() => ({}))) as Record<string, string>;
        return {
          success: false,
          error: new Error(
            errorBody['error'] ?? `HTTP ${String(response.status)}`,
          ),
        };
      }

      const data = (await response.json()) as T;
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Network error'),
      };
    }
  }

  return {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
    put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
    patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
    delete: <T>(path: string) => request<T>('DELETE', path),
  };
}
